
import {
  computed, defineComponent, PropType, toRefs, ref, watch,
} from 'vue-demi';
import AdmSymbol from '../AdmSymbol';
import './text-field.scss';

export default defineComponent({
  name: 'adm-text-field',
  components: {
    AdmSymbol,
  },
  emits: ['update:modelValue', 'leadingIconClick', 'trailingIconClick', 'blur', 'focus', 'keypress'],
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    modelValue: {
      type: String,
      default: '',
    },
    size: {
      type: String as PropType<'small'|'normal'>,
      default: 'normal',
    },
    type: {
      type: String as PropType<'text'|'password'|'date'|'datetime-local'|'time'|'number'|'month'|'week'|'search'>,
      default: 'text',
    },
    dense: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    trailingIcon: {
      type: String,
      default: '',
    },
    leadingIcon: {
      type: String,
      default: '',
    },
    helperText: {
      type: String,
      default: '',
    },
    error: {
      type: String,
      default: '',
    },
    autocomplete: {
      type: String,
      default: undefined,
    },
    name: {
      type: String,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    formatter: {
      type: Function,
      default: null,
    },
  },
  setup(props, { emit }) {
    const {
      trailingIcon,
      leadingIcon,
      disabled, dense,
      size,
      label,
      placeholder,
      helperText,
      error,
      formatter,
      modelValue,
    } = toRefs(props);
    const isFocused = ref(false);
    const value = ref<string>(modelValue.value);

    watch(value, (nextValue) => {
      let formattedValue = nextValue;
      if (typeof formatter.value === 'function') {
        formattedValue = formatter.value(nextValue);
        value.value = formattedValue;
      }

      emit('update:modelValue', formattedValue);
    });

    watch(modelValue, (nextModelValue) => {
      if (nextModelValue !== value.value) {
        value.value = nextModelValue;
      }
    });

    const classes = computed(() => [
      'adm-text-field',
      `adm-text-field--${size.value}`,
      {
        'adm-text-field--with-value': !!value.value || isFocused.value,
        'adm-text-field--with-icon-lead': !!leadingIcon.value,
        'adm-text-field--with-icon-trail': !!trailingIcon.value,
        'adm-text-field--disabled': disabled.value,
        'adm-text-field--dense': dense.value,
        'adm-text-field--error': !!error.value,
      },
    ]);

    const placeholderText = computed(() => {
      if (dense.value) {
        return label.value || placeholder.value || undefined;
      }

      if (isFocused.value) {
        return placeholder.value || undefined;
      }

      return label.value ? undefined : placeholder.value;
    });

    const descriptionText = computed(() => error.value || helperText.value || '');

    const onFocus = (e: Event) => {
      isFocused.value = true;
      emit('focus', e);
    };

    const onBlur = (e: Event) => {
      isFocused.value = false;
      emit('blur', e);
    };

    return {
      classes,
      onFocus,
      onBlur,
      placeholderText,
      descriptionText,
      value,
    };
  },
});
